import React, { useContext } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import useUserTokenPrivileges from '../hooks/useUserTokenPrivileges';
import { getDefaultVenuePath } from '../../lib/consts/navigation';
import { UserContext } from '../../context/UserContext';
import { cerrarSesion } from '../../lib/authSession';

/** Cuenta válida cuya pantalla por defecto es justo la que no le está permitida (un
 *  `client` con token, por ejemplo): no hay a dónde mandarla sin volver aquí, así que
 *  no se navega: se dice y se ofrece salir. */
function SinAcceso() {
  const [, setToken] = useContext(UserContext);
  const salir = () => {
    cerrarSesion();
    setToken(null);
  };
  return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <div className="text-center max-w-md">
        <p className="text-lg font-semibold text-[var(--foreground)]">Tu cuenta no tiene acceso a esta pantalla</p>
        <p className="mt-2 text-sm text-[var(--muted-foreground)]">Pide al responsable del club que revise tu perfil, o entra con otra cuenta.</p>
        <button
          type="button"
          onClick={salir}
          className="mt-4 inline-flex h-9 items-center rounded-sm border border-[var(--border)] px-4 text-sm text-[var(--foreground)] hover:bg-[var(--muted)]"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

/**
 * A route guard that only renders children if the user is authenticated
 * and has one of the allowed roles. Otherwise, it redirects.
 *
 * @param {string[]} allowedRoles - array of roles permitted to access the route
 * @returns {JSX.Element|null}
 */
const PrivateRoutes = ({ allowedRoles = [] }) => {
  const { user, token, accessPrivileges, venueType, isLoading } = useUserTokenPrivileges();
  const location = useLocation();

  // While we fetch user/token info, show a loading indicator
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // If not logged in, redirect to login with return path
  if (!token || !user) {
    return (
      <Navigate to="/login" state={{ from: location }} replace />
    );
  }

  // Logueado pero sin permiso para ESTA ruta: a su pantalla, no al login. Mandarle al
  // login con `from` era un bucle: Login, viendo sesión válida, le devolvía a `from`,
  // y de aquí otra vez al login — antes lo frenaba un fetch por vuelta, ahora nada.
  if (allowedRoles.length > 0 && !allowedRoles.includes(accessPrivileges)) {
    const propia = accessPrivileges === 'superadmin' ? '/my_shops' : getDefaultVenuePath(venueType);
    // Si su propia pantalla es justo esta, navegar a ella o al login vuelve aquí (Login
    // recalcula la misma ruta): se corta sin navegar. Se compara sin la query
    // (`/dispenser?mode=productos` vive en `/dispenser`).
    if (location.pathname === propia.split('?')[0]) return <SinAcceso />;
    return <Navigate to={propia} replace />;
  }

  // Authorized: render child routes
  return <Outlet />;
};

export default PrivateRoutes;
